import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import MaterialIcon from "../../../components/MaterialIcon";
import { callAPI } from "../../../helper/apiUtils";
import { BASE_URL, getAvatarInitials } from "../../../helper/constants";
import { $crud } from "../../../helper/CrudFactory";
import { Avatar, Card, Chip, Skeleton } from "./CockpitPrimitives";
import { bookmarkDeadlineLabel, calendarWeek, fill, formatDayTile, formatFromNow, formatGreetingDate, formatRelativeDay, formatShortDate, formatTime, fullName, shiftWeeks, weekDayKeys } from "../cockpitFormat";
// The task rows look like the Aufgaben list's (the mobile layout), with its parts and styles.
import { AssignDate, CalendarButton, TodoCheck, TodoTag } from "../../Feeds/ToDoProParts";
import { useTodoCalendar } from "../../Feeds/useTodoCalendar";
import { taskCheckState } from "../../Feeds/todoProModel";

function bucketChip(item, strings) {
  switch (item.bucket) {
    case "overdue":
      return <Chip tone="red">{strings.cockpit_chip_overdue || "Überfällig"}</Chip>;
    case "today":
      return <Chip tone="accent">{strings.cockpit_chip_today || "Heute"}</Chip>;
    case "requested":
      return <Chip tone="slate">{strings.cockpit_chip_requested || "Abgefordert"}</Chip>;
    case "upcoming":
      return <Chip tone="neutral">{formatShortDate(item.createdAt)}</Chip>;
    default:
      return null;
  }
}

function todoMeta(item, strings, language, currentUserId) {
  const bits = [];
  if (item.bucket === "overdue") {
    bits.push(fill(strings.cockpit_todo_due || "Frist {{when}}", { when: formatRelativeDay(item.createdAt, strings, language).toLowerCase() }));
  } else if (item.bucket === "today") {
    bits.push(fill(strings.cockpit_todo_at || "{{time}} Uhr", { time: formatTime(item.createdAt) }));
  } else if (item.bucket === "requested") {
    const others = (item.assignedTo || []).filter((a) => String(a.id) !== String(currentUserId));
    if (others.length) {
      bits.push(fill(strings.cockpit_todo_requested_from || "bei {{name}}", { name: fullName(others[0]) + (others.length > 1 ? ` +${others.length - 1}` : "") }));
    }
    bits.push(fill(strings.cockpit_todo_since || "seit {{when}}", { when: formatFromNow(item.updatedAt || item.createdAt, language) }));
  }
  if (item.groupName && !item.roomName) bits.push(item.groupName);
  return bits.filter(Boolean).join(" · ");
}

/**
 * A task's text, clamped to three lines. When it does not fit, a "Mehr anzeigen"
 * link at the end opens the whole text in place.
 */
export function TaskTitle({ text, strings }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [clamped, setClamped] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return undefined;
    const measure = () => setClamped(el.scrollHeight - el.clientHeight > 1);
    measure();
    // The card narrows with the window, which can clamp a title that fit before.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, open]);

  return (
    <>
      <div ref={ref} className={`cockpit-prio-title${open ? " cockpit-prio-title--open" : ""}`}>{text}</div>
      {clamped || open ? (
        <button
          type="button"
          className="cockpit-prio-more"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
        >
          {open ? strings.show_less || "Weniger anzeigen" : strings.show_more || "Mehr anzeigen"}
        </button>
      ) : null}
    </>
  );
}

function TodoRow({ item, strings, language, currentUserId, onOpen, onComplete, completing, chevron = true }) {
  // Every task belongs to a project; its name heads the row.
  const roomLabel = item.roomName || (item.isBookmarked ? strings.cockpit_todo_no_date || "Ohne Datum" : "");
  return (
    <div className={`cockpit-prio cockpit-prio--${item.bucket || "open"}`} onClick={() => onOpen && onOpen(item)} role="button">
      <span className="cockpit-prio-bar" />
      <div className="cockpit-prio-body">
        {roomLabel ? <div className="cockpit-prio-project">{roomLabel}</div> : null}
        <TaskTitle text={item.description} strings={strings} />
        <div className="cockpit-prio-meta">
          {bucketChip(item, strings)}
          <span>{todoMeta(item, strings, language, currentUserId)}</span>
        </div>
      </div>
      <div className="cockpit-prio-end">
        {onComplete ? (
          <button
            type="button"
            className="kj-btn kj-btn--icon kj-btn--neutral"
            title={strings.cockpit_todo_mark_done || "Als erledigt markieren"}
            aria-label={strings.cockpit_todo_mark_done || "Als erledigt markieren"}
            disabled={completing}
            onClick={(e) => {
              e.stopPropagation();
              onComplete(item);
            }}
          >
            <MaterialIcon icon={completing ? "hourglass_empty" : "check_circle"} size={20} color="currentColor" weight={300} />
          </button>
        ) : (
          chevron ? <MaterialIcon icon="chevron_right" size={18} color="#a7aeba" /> : null
        )}
      </div>
    </div>
  );
}

export const MY_TASKS_CARD_ID = "cockpit-my-tasks";

/**
 * The week of the picked day over Meine Aufgaben, Monday to Sunday: a tile per
 * day with its short weekday, its date and a dot when something falls on it
 * (`marks[day]`: "appointment" accent, "tasks" orange). The picked day is the
 * dark tile, today keeps an outline. The arrows switch to the week before or
 * after (today there when it is that week, else its Monday); "Heute" comes
 * back to this week.
 */
function DayStrip({ day, today, marks, onPick, language, strings }) {
  const days = useMemo(
    () => weekDayKeys(day).map((key) => ({
      key,
      ...formatDayTile(key, language),
      label: formatGreetingDate(new Date(`${key}T12:00:00`), language),
    })),
    [day, language]
  );
  const toWeek = (weeks) => {
    const week = weekDayKeys(shiftWeeks(day, weeks));
    onPick(week.includes(today) ? today : week[0]);
  };
  const thisWeek = days.some((d) => d.key === today);
  const prevLabel = strings.cockpit_prev_week || "Vorherige Woche";
  const nextLabel = strings.cockpit_next_week || "Nächste Woche";
  return (
    <div className="cockpit-daystrip">
      <button type="button" className="kj-btn kj-btn--icon kj-btn--neutral cockpit-daystrip-arrow" title={prevLabel} aria-label={prevLabel} onClick={() => toWeek(-1)}>
        <MaterialIcon icon="chevron_left" size={22} color="currentColor" weight={400} />
      </button>
      <div className="cockpit-daystrip-days">
        {days.map((d) => {
          const on = d.key === day;
          const mark = marks && marks[d.key];
          return (
            <button
              key={d.key}
              type="button"
              className={`cockpit-daytile${on ? " is-on" : ""}${d.key === today ? " is-today" : ""}`}
              aria-pressed={on}
              aria-label={d.label}
              title={d.label}
              onClick={() => onPick(d.key)}
            >
              <span className="cockpit-daytile-weekday">{d.weekday}</span>
              <span className="cockpit-daytile-date">{d.date}</span>
              <span className={`cockpit-daytile-dot${mark ? ` cockpit-daytile-dot--${mark}` : ""}`} />
            </button>
          );
        })}
      </div>
      <button type="button" className="kj-btn kj-btn--icon kj-btn--neutral cockpit-daystrip-arrow" title={nextLabel} aria-label={nextLabel} onClick={() => toWeek(1)}>
        <MaterialIcon icon="chevron_right" size={22} color="currentColor" weight={400} />
      </button>
      {!thisWeek ? (
        <button type="button" className="kj-btn kj-btn--secondary kj-btn--sm cockpit-daystrip-today" onClick={() => onPick(today)}>
          {strings.cockpit_chip_today || "Heute"}
        </button>
      ) : null}
    </div>
  );
}

/**
 * "Heute zu erledigen" beside the daily report: today's dated tasks, then
 * `hostRef` — the node the report draws its bookmarked tasks into (portal).
 * `actionRef` is the header slot the report puts its "Mehrere löschen" into.
 * `day`/`today`/`dayMarks`/`onPickDay`: the day strip under the title — the
 * date in the title is the picked day.
 */
export function CockpitMyTasksCard({ strings, dateLabel, items = [], loading, currentUserId, language, onOpenItem, hostRef, actionRef, groups = null, groupCounts = null, onToggleGroup = null, day = null, today = null, dayMarks = null, onPickDay = null }) {
  // The cockpit's two groups (COCKPIT_TASK_GROUPS; priorityBookmarks imports this file).
  const groupTitles = {
    tasks: strings.cockpit_group_tasks || "Fristen & Aufgaben",
    appointment: strings.cockpit_group_appointments || "Termine",
  };
  return (
    <Card className="cockpit-mytasks">
      {/* The card's title, set like "Management-Fazit" in the report card
          beside it; its row lines up with that card's title row. */}
      <div id={MY_TASKS_CARD_ID} className="cockpit-mytasks-head">
        <div className="cockpit-mytasks-head-row">
          {/* Title and date are one heading: the bulk controls wrap below both. */}
          <div className="cockpit-mytasks-heading">
            <h4 className="cockpit-mytasks-title">{strings.cockpit_my_tasks_title || "Meine Aufgaben"}</h4>
            {dateLabel ? (
              <div className="cockpit-mytasks-date">
                {dateLabel}
                {/* The calendar week of the day shown: "· KW 45". */}
                {day ? (
                  <span className="cockpit-mytasks-week">
                    {" · "}
                    {fill(strings.cockpit_calendar_week || "KW {{week}}", { week: calendarWeek(day) })}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          {/* The report fills this with "Mehrere löschen" or, while picking
              rows, with the bulk controls (they wrap onto their own line). */}
          <div ref={actionRef} className="cockpit-mytasks-action" />
        </div>
        {day && today && onPickDay ? (
          <DayStrip day={day} today={today} marks={dayMarks} onPick={onPickDay} language={language} strings={strings} />
        ) : null}
        {/* What the list shows: the same switches as the tiles above. */}
        {groups && onToggleGroup ? (
          <div className="cockpit-mytasks-filter" role="group" aria-label={strings.cockpit_my_tasks_title || "Meine Aufgaben"}>
            {["tasks", "appointment"].map((key) => {
              const on = groups.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  className={`cockpit-mytasks-chip${on ? " is-on" : ""}`}
                  aria-pressed={on}
                  // The last group switched on stays on.
                  disabled={on && groups.length === 1}
                  onClick={() => onToggleGroup(key)}
                >
                  <MaterialIcon icon={on ? "check" : "add"} size={16} color="currentColor" weight={on ? 600 : 400} />
                  <span>{groupTitles[key]}</span>
                  {groupCounts && groupCounts[key] != null ? <span className="cockpit-mytasks-chip-count">{groupCounts[key]}</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <div className="cockpit-mytasks-body">
        {loading && !items.length ? (
          <div className="cockpit-list-skeleton cockpit-mytasks-today">
            {[0, 1].map((i) => (
              <div key={i} className="cockpit-prio">
                <span className="cockpit-prio-bar cockpit-prio-bar--skeleton" />
                <div className="cockpit-prio-body">
                  <Skeleton width="30%" height={10} />
                  <Skeleton width="70%" height={14} className="cockpit-skeleton--gap" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length ? (
          <section className="cockpit-mytasks-today">
            {/* Same group header as the bookmarked tasks below. */}
            <div className="cockpit-bookmark-group">
              <span>{strings.cockpit_today_tasks_group || "Aufgaben heute"}</span>
              <span className="cockpit-bookmark-group-count">{items.length}</span>
            </div>
            <div className="cockpit-mytasks-list">
              {items.map((item) => (
                <TodoRow key={item.todoId} item={item} strings={strings} language={language} currentUserId={currentUserId} onOpen={onOpenItem} chevron={false} />
              ))}
            </div>
          </section>
        ) : null}
        <div ref={hostRef} className="cockpit-mytasks-bookmarks" />
      </div>
    </Card>
  );
}

const ASSIGN_PAGE_SIZE = 50;

/** Global contact search — the same directory the to-do page assigns from. */
function AssignPicker({ strings, assigned = [], busy, onCancel, onConfirm }) {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(() => assigned.filter((user) => user && user.id));
  const listRef = useRef(null);
  const loadingRef = useRef(false);
  const queryRef = useRef("");

  useEffect(() => {
    let cancelled = false;
    queryRef.current = query;
    setLoading(true);
    const timer = setTimeout(() => {
      callAPI(`/user/allUsersSearch?limit=${ASSIGN_PAGE_SIZE}&page=1&query=${encodeURIComponent(query)}`, {}, "GET")
        .then(({ data }) => {
          if (cancelled) return;
          const list = data?.data?.users || data?.users || (Array.isArray(data) ? data : []);
          setUsers(Array.isArray(list) ? list : []);
          setTotal(Number(data?.data?.totalCnt ?? data?.totalCnt) || 0);
          setPage(1);
          if (listRef.current) listRef.current.scrollTop = 0;
        })
        .catch(() => {
          if (cancelled) return;
          setUsers([]);
          setTotal(0);
          setPage(1);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  // The directory is far larger than one page, so pull the rest in as the list scrolls.
  const hasMore = !loading && users.length < total;

  const loadMore = () => {
    if (loadingRef.current || !hasMore) return;
    const next = page + 1;
    const forQuery = query;
    loadingRef.current = true;
    callAPI(`/user/allUsersSearch?limit=${ASSIGN_PAGE_SIZE}&page=${next}&query=${encodeURIComponent(query)}`, {}, "GET")
      .then(({ data }) => {
        if (queryRef.current !== forQuery) return;
        const list = data?.data?.users || data?.users || (Array.isArray(data) ? data : []);
        if (!Array.isArray(list) || !list.length) {
          setTotal(users.length);
          return;
        }
        setUsers((current) => {
          const seen = new Set(current.map((user) => String(user.id)));
          return [...current, ...list.filter((user) => user && !seen.has(String(user.id)))];
        });
        setPage(next);
      })
      .catch(() => {})
      .finally(() => {
        loadingRef.current = false;
      });
  };

  const onListScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 60) loadMore();
  };

  const toggle = (user) => {
    setPicked((current) =>
      current.some((entry) => String(entry.id) === String(user.id))
        ? current.filter((entry) => String(entry.id) !== String(user.id))
        : [...current, user]
    );
  };

  return (
    <div className="cockpit-bookmark-assign" onClick={(e) => e.stopPropagation()}>
      <input
        className="cockpit-bookmark-search"
        value={query}
        autoFocus
        onChange={(e) => setQuery(e.target.value)}
        placeholder={strings.search || "Suchen"}
      />
      {/* The assignees, removable without hunting for them in the search list. */}
      <div className="cockpit-bookmark-picked">
        {picked.length ? (
          picked.map((user) => (
            <button
              type="button"
              key={user.id}
              className="cockpit-bookmark-picked-chip"
              title={strings.remove || "Entfernen"}
              aria-label={`${strings.remove || "Entfernen"}: ${fullName(user)}`}
              onClick={() => toggle(user)}
            >
              <Avatar user={user} size={18} />
              <span className="cockpit-bookmark-user-name">{fullName(user)}</span>
              <MaterialIcon icon="close" size={14} color="currentColor" />
            </button>
          ))
        ) : (
          <span className="cockpit-bookmark-picked-empty">{strings.cockpit_assign_none || "Niemand zugewiesen"}</span>
        )}
      </div>
      <div className="cockpit-bookmark-users" ref={listRef} onScroll={onListScroll}>
        {loading ? (
          [0, 1, 2].map((i) => (
            <div key={i} className="cockpit-bookmark-user cockpit-bookmark-user--loading">
              <Skeleton width={22} height={22} className="cockpit-skeleton--round" />
              <Skeleton width={`${55 + i * 12}%`} height={10} />
            </div>
          ))
        ) : users.length ? (
          <>
            {users.map((user) => {
              const on = picked.some((entry) => String(entry.id) === String(user.id));
              return (
                <button
                  type="button"
                  key={user.id}
                  className={`cockpit-bookmark-user${on ? " cockpit-bookmark-user--on" : ""}`}
                  onClick={() => toggle(user)}
                >
                  <Avatar user={user} size={22} />
                  <span className="cockpit-bookmark-user-name">{fullName(user)}</span>
                  {on ? <MaterialIcon icon="check" size={16} color="currentColor" /> : null}
                </button>
              );
            })}
            {hasMore ? (
              <div className="cockpit-bookmark-user cockpit-bookmark-user--loading">
                <Skeleton width={22} height={22} className="cockpit-skeleton--round" />
                <Skeleton width="60%" height={10} />
              </div>
            ) : null}
          </>
        ) : (
          <div className="cockpit-bookmark-users-note">{strings.no_users_found || "Keine Benutzer gefunden"}</div>
        )}
      </div>
      <div className="cockpit-bookmark-save">
        {/* An empty pick is a valid choice: it clears the assignment. */}
        <button
          type="button"
          className="kj-btn kj-btn--primary kj-btn--sm cockpit-bookmark-save-btn"
          disabled={busy}
          onClick={() => onConfirm(picked)}
        >
          {strings.confirm || "Bestätigen"}
        </button>
        <button type="button" className="kj-btn kj-btn--secondary kj-btn--sm cockpit-bookmark-ghost-btn" onClick={onCancel}>
          {strings.cancel || "Abbrechen"}
        </button>
      </div>
    </div>
  );
}

/** The room's own badge, the one the sidebar pins show: photo or themed initials. */
function RoomAvatar({ room }) {
  const name = (room && room.name) || "";
  if (room && room.image) {
    return (
      <img
        className="cockpit-room-avatar"
        src={String(room.image).startsWith("http") ? room.image : BASE_URL + room.image}
        alt={name}
      />
    );
  }
  return (
    <span className="cockpit-room-avatar cockpit-room-avatar--tinted" style={{ "--cockpit-room-color": (room && room.themeColor) || "#737373" }}>
      {getAvatarInitials(name)}
    </span>
  );
}

/**
 * The two endpoints that together hold every room the user belongs to:
 * `/building/my` is external only, Kjuup+ rooms live behind the internal one.
 * Fetched once per page life and shared by every picker on the cockpit.
 */
let allRoomsRequest = null;

export function loadAllRooms() {
  if (allRoomsRequest) return allRoomsRequest;
  allRoomsRequest = (async () => {
    const [ext, int] = await Promise.all([
      callAPI("/building/my", {}, "GET"),
      callAPI("/building/internalProjects", {}, "GET"),
    ]);
    const list = [];
    if (ext?.data?.status) {
      (ext.data.buildings || []).forEach((room) => list.push({ ...room, isInternal: false }));
    }
    if (int?.data?.status) {
      (int.data.foundUserCompany || []).forEach((company) => {
        (company.userProjectList || []).forEach((room) => list.push({ ...room, isInternal: true }));
      });
    }
    return list;
  })();
  allRoomsRequest.catch(() => {
    allRoomsRequest = null;
  });
  return allRoomsRequest;
}

/**
 * Every room the user can file a priority into, external and internal in one
 * list. The cockpit's own `rooms` is capped at eight and the Redux lists are
 * only as fresh as the last page that loaded them, so both are shown right
 * away and the fetched list fills in whatever they missed.
 */
function useAllRooms(cockpitRooms) {
  const external = useSelector((state) => state.projects);
  const internal = useSelector((state) => state.internalProjectsAll);
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    let alive = true;
    loadAllRooms().then(
      (list) => {
        if (alive) setFetched(list);
      },
      () => {}
    );
    return () => {
      alive = false;
    };
  }, []);

  const rooms = useMemo(() => {
    const byId = new Map();
    const add = (room, isInternal) => {
      if (!room || room.id == null) return;
      const key = String(room.id);
      const prev = byId.get(key) || {};
      byId.set(key, {
        id: room.id,
        name: room.name || prev.name || "",
        image: room.image || prev.image || null,
        themeColor: room.themeColor || prev.themeColor || null,
        isInternal: isInternal != null ? isInternal : !!room.isInternal,
      });
    };
    (cockpitRooms || []).forEach((room) => add(room, room.isInternal));
    (external || []).forEach((room) => add(room, false));
    (internal || []).forEach((company) => (company.userProjectList || []).forEach((room) => add(room, true)));
    (fetched || []).forEach((room) => add(room, room.isInternal));
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [cockpitRooms, external, internal, fetched]);

  return { rooms, loading: fetched === null };
}

/**
 * Files a bookmarked priority under one of the user's project rooms. A room is
 * a single choice, so picking one saves straight away.
 */
function RoomPicker({ strings, rooms, currentId, busy, onCancel, onConfirm }) {
  const [query, setQuery] = useState("");
  const { rooms: all, loading } = useAllRooms(rooms);
  const needle = query.trim().toLowerCase();
  const list = needle
    ? all.filter((room) => String(room.name || "").toLowerCase().includes(needle))
    : all;

  return (
    <div className="cockpit-bookmark-assign" onClick={(e) => e.stopPropagation()}>
      <input
        className="cockpit-bookmark-search"
        value={query}
        autoFocus
        onChange={(e) => setQuery(e.target.value)}
        placeholder={strings.search || "Suchen"}
      />
      <div className="cockpit-bookmark-users">
        {/* Another project only: a task always belongs to one. */}
        {list.length ? (
          list.map((room) => {
            const on = currentId && String(currentId) === String(room.id);
            return (
              <button
                type="button"
                key={room.id}
                className={`cockpit-bookmark-user${on ? " cockpit-bookmark-user--on" : ""}`}
                disabled={busy}
                onClick={() => onConfirm(room)}
              >
                <RoomAvatar room={room} />
                <span className="cockpit-bookmark-user-name">{room.name}</span>
                {on ? <MaterialIcon icon="check" size={16} color="currentColor" /> : null}
              </button>
            );
          })
        ) : loading ? (
          <div className="cockpit-bookmark-users-note">{strings.email_loading || "Wird geladen …"}</div>
        ) : (
          <div className="cockpit-bookmark-users-note">{strings.no_project_found || "Kein Projekt gefunden"}</div>
        )}
      </div>
      <div className="cockpit-bookmark-save">
        <button type="button" className="kj-btn kj-btn--secondary kj-btn--sm cockpit-bookmark-ghost-btn" onClick={onCancel}>
          {strings.cancel || "Abbrechen"}
        </button>
      </div>
    </div>
  );
}

// Scheduled priorities land at 2 pm; the hour can be changed in the tasks area.
const BOOKMARK_DEFAULT_TIME = "14:00";

/** A task's date as the local "YYYY-MM-DD", else "". */
function localDay(value) {
  const date = new Date(value || "");
  if (!value || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * "…" beside a row: its actions as a small menu. `items`: { key, icon, label,
 * onClick, danger?, disabled? }; empty entries are skipped.
 */
export function RowMenu({ items, strings, disabled }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const entries = (items || []).filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!entries.length) return null;
  const label = strings.cockpit_row_more_actions || "Weitere Aktionen";
  return (
    <div className="cockpit-row-menu" ref={rootRef}>
      <button
        type="button"
        className="kj-btn kj-btn--icon kj-btn--neutral"
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <MaterialIcon icon="more_vert" size={20} color="currentColor" weight={400} />
      </button>
      {open ? (
        <div className="cockpit-row-menu-list" role="menu">
          {entries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="menuitem"
              className={`cockpit-row-menu-item${entry.danger ? " cockpit-row-menu-item--danger" : ""}`}
              disabled={entry.disabled}
              onClick={() => {
                setOpen(false);
                entry.onClick();
              }}
            >
              <MaterialIcon icon={entry.icon} size={18} color="currentColor" weight={300} />
              <span>{entry.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A bookmarked (dateless) to-do or a task the daily report created, with its
 * inline editor. `actions` adds entries to its "…" menu — the daily report
 * puts its "jump to source" there. `calendar` ({ added, loading, needsDate },
 * useTodoCalendar) shows the calendar button, a click is `onCalendar(item)`.
 */
export function BookmarkRow({ item, strings, rooms, defaultDate, busy, actions, selecting, selected, onSelect, onSchedule, onRename, onAssign, onAnswerSuggestion, onSetRoom, onDelete, onOpen, onComplete, calendar, onCalendar }) {
  const currentUser = useSelector((state) => state.user);
  const [date, setDate] = useState(defaultDate || "");
  // "" | "edit" | "assign" | "room" | "schedule" — one panel at a time keeps the row short.
  const [panel, setPanel] = useState("");
  const [text, setText] = useState(item.description || "");
  const textRef = useRef(null);
  const dateRef = useRef(null);
  const assigned = item.assignedTo || [];
  // Tasks the daily report created come with a date; bookmarks have none.
  const scheduled = item.isBookmarked != null && !Number(item.isBookmarked);
  // The day as on the Aufgaben list: "Überfällig" + "27.10. · 14:00 Uhr",
  // "Heute" + the time, or the day chip + the time.
  const day = scheduled ? localDay(item.createdAt) : "";
  const todayKey = localDay(new Date());
  const bucket = !day ? "open" : day < todayKey ? "overdue" : day === todayKey ? "today" : "upcoming";
  const time = scheduled ? fill(strings.cockpit_todo_at || "{{time}} Uhr", { time: formatTime(item.createdAt) }) : "";
  const shortDay = day ? `${day.slice(8, 10)}.${day.slice(5, 7)}.` : "";
  // The tick, as on the list: only my own open part can be ticked off here.
  const checkState = taskCheckState(item, currentUser && currentUser.id);
  const check = checkState === "open" && onComplete ? "open" : checkState === "done" || checkState === "confirmed" ? checkState : null;
  // A member the report task names, waiting for the user's confirmation.
  const proposal = item.suggestedAssignee;
  const openProposal =
    !!onAnswerSuggestion &&
    !!proposal &&
    proposal.status === "open" &&
    !assigned.some((user) => user && String(user.id) === String(proposal.userId));

  // The editor opens at one line and grows into the full text, so nothing is
  // hidden behind a scrollbar. Sizing after a frame lets the height animate.
  useEffect(() => {
    if (panel !== "edit") return undefined;
    const frame = requestAnimationFrame(() => {
      const el = textRef.current;
      if (!el) return;
      el.style.height = `${el.scrollHeight}px`;
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
    return () => cancelAnimationFrame(frame);
  }, [panel]);

  // Opening the scheduler drops the user straight into the calendar instead of
  // making them click the field they just asked for.
  useEffect(() => {
    if (panel !== "schedule") return undefined;
    const frame = requestAnimationFrame(() => {
      const el = dateRef.current;
      if (!el || typeof el.showPicker !== "function") return;
      try {
        el.showPicker();
      } catch (_) {
        el.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [panel]);

  const grow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const saveText = async () => {
    const next = text.trim();
    if (!next || next === item.description) {
      setPanel("");
      return;
    }
    if (await onRename(item, next)) setPanel("");
  };

  const saveAssignees = async (users) => {
    if (await onAssign(item, users)) setPanel("");
  };

  const saveRoom = async (room) => {
    if (await onSetRoom(item, room)) setPanel("");
  };

  return (
    // .tdp scopes the Aufgaben list's row styles.
    <div className="tdp cockpit-task">
      <div
        className={`tdp-row cockpit-task-row${panel ? " is-editing" : ""}${selecting && selected ? " is-adj-selected" : ""}`}
        // With a long list, hitting the 16px box for every row is tedious, so
        // the whole row toggles — except where the user aimed at a control.
        // Otherwise the row opens the task on the Aufgaben page.
        onClick={(e) => {
          if (e.target.closest("button, input, a, textarea, select, [role='menu']")) return;
          if (selecting) onSelect();
          else if (!panel && onOpen) onOpen(item);
        }}
        role={onOpen && !selecting ? "button" : undefined}
      >
        {selecting ? (
          <span className="tdp-check">
            <input
              type="checkbox"
              className="cockpit-prio-check"
              checked={!!selected}
              aria-label={item.description || strings.select_all || "Auswählen"}
              onChange={onSelect}
            />
          </span>
        ) : (
          <TodoCheck
            check={check}
            busy={busy && check === "open"}
            onClick={() => onComplete(item)}
            label={strings.cockpit_todo_mark_done || "Als erledigt markieren"}
          />
        )}
        <div className="tdp-row-body">
          {(() => {
            // A report task was dated from that deadline; its own date wins
            // once the user moves it.
            const dueDay = item.bookmarkDeadline && scheduled ? localDay(item.createdAt) || item.bookmarkDeadline : item.bookmarkDeadline;
            const due = dueDay
              ? `${item.bookmarkCategory === "appointment"
                ? strings.cockpit_todo_appointment || "Termin"
                : strings.cockpit_todo_deadline || "Frist"} ${bookmarkDeadlineLabel(dueDay, strings)}`
              : item.bookmarkDeadlineNote === "define"
                ? strings.cockpit_todo_deadline_define || "Frist definieren"
                : "";
            const caption = [item.bookmarkProject || item.roomName, due].filter(Boolean).join(" · ");
            return caption ? <div className="tdp-row-room">{caption}</div> : null;
          })()}
          {panel === "edit" ? (
            <textarea
              ref={textRef}
              className="cockpit-bookmark-text"
              value={text}
              autoFocus
              rows={1}
              onChange={(e) => {
                setText(e.target.value);
                grow(e.target);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") setPanel("");
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  saveText();
                }
              }}
            />
          ) : (
            <div className="tdp-row-title">{item.description}</div>
          )}
          {scheduled && panel !== "edit" ? (
            <div className="tdp-row-meta">
              {bucket === "overdue" ? <TodoTag label={strings.cockpit_chip_overdue || "Überfällig"} tone="err" /> : null}
              {bucket === "today" ? <TodoTag label={strings.cockpit_chip_today || "Heute"} tone="new" /> : null}
              {bucket === "upcoming" ? <TodoTag label={shortDay} tone="muted" /> : null}
              <span className={`tdp-due${bucket === "overdue" ? " is-overdue" : ""}`}>
                <MaterialIcon icon="schedule" size={16} />
                <span className="tdp-due-text">{bucket === "overdue" ? `${shortDay} · ${time}` : time}</span>
              </span>
            </div>
          ) : null}
          {/* A bookmark: "Ohne Datum" and "Termin zuweisen" on one line. */}
          {!scheduled && !panel && !selecting ? (
            <div className="tdp-row-meta tdp-row-bmmeta">
              <span className="tdp-due">
                <MaterialIcon icon="event_busy" size={16} />
                <span className="tdp-due-text">{strings.cockpit_todo_no_date || "Ohne Datum"}</span>
              </span>
              {onSchedule ? (
                <AssignDate
                  label={strings.needs_date || "Termin zuweisen"}
                  busy={busy}
                  onPick={(picked) => onSchedule(item, picked, BOOKMARK_DEFAULT_TIME)}
                />
              ) : null}
            </div>
          ) : null}
          {openProposal && panel !== "edit" ? (
            <div className="cockpit-assign-proposal">
              <MaterialIcon icon="person_search" size={16} color="currentColor" weight={400} />
              <span>
                {fill(strings.cockpit_assign_proposal || "{{name}} erkannt – zuweisen?", { name: proposal.name })}
              </span>
              <button
                type="button"
                className="kj-btn kj-btn--primary kj-btn--sm"
                disabled={busy}
                onClick={() => onAnswerSuggestion(item, true)}
              >
                {strings.cockpit_assign_proposal_confirm || "Zuweisen"}
              </button>
              <button
                type="button"
                className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--sm"
                disabled={busy}
                onClick={() => onAnswerSuggestion(item, false)}
              >
                {strings.cockpit_assign_proposal_dismiss || "Ignorieren"}
              </button>
            </div>
          ) : null}
          {panel === "edit" ? (
            <div className="cockpit-bookmark-save">
              <button type="button" className="kj-btn kj-btn--primary kj-btn--sm cockpit-bookmark-save-btn" disabled={busy} onClick={saveText}>
                {strings.save_text || "speichern"}
              </button>
              <button type="button" className="kj-btn kj-btn--secondary kj-btn--sm cockpit-bookmark-ghost-btn" onClick={() => setPanel("")}>
                {strings.cancel || "Abbrechen"}
              </button>
            </div>
          ) : panel === "assign" ? (
            <AssignPicker
              strings={strings}
              assigned={assigned}
              busy={busy}
              onCancel={() => setPanel("")}
              onConfirm={saveAssignees}
            />
          ) : panel === "room" ? (
            <RoomPicker
              strings={strings}
              rooms={rooms}
              currentId={item.roomId || item.buildingList || null}
              busy={busy}
              onCancel={() => setPanel("")}
              onConfirm={saveRoom}
            />
          ) : panel === "schedule" ? (
            /* A bookmarked priority needs a day before it can leave the list as a
               task. The hour is always 14:00 — it can be changed in the tasks area. */
            <div className="cockpit-bookmark-save cockpit-bookmark-save--when">
              <input
                ref={dateRef}
                type="date"
                className="cockpit-bookmark-date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                aria-label={strings.cockpit_priority_date || "Datum"}
              />
              <button
                type="button"
                className="kj-btn kj-btn--primary kj-btn--sm cockpit-bookmark-save-btn"
                disabled={!date || busy}
                onClick={() => onSchedule(item, date, BOOKMARK_DEFAULT_TIME)}
              >
                {strings.save_text || "speichern"}
              </button>
              <button type="button" className="kj-btn kj-btn--secondary kj-btn--sm cockpit-bookmark-ghost-btn" onClick={() => setPanel("")}>
                {strings.cancel || "Abbrechen"}
              </button>
            </div>
          ) : null}
        </div>
        <div className="cockpit-prio-end">
          {calendar && onCalendar && !selecting && !panel ? (
            <CalendarButton
              calendar={calendar}
              labels={{
                calendarAdd: strings.todo_calendar_add || "Zum Kalender hinzufügen",
                calendarRemove: strings.todo_calendar_remove || "Aus dem Kalender entfernen",
              }}
              onClick={() => onCalendar(item)}
            />
          ) : null}
          <RowMenu
            strings={strings}
            disabled={busy}
            items={[
              ...(actions || []),
              {
                key: "edit",
                icon: "edit",
                label: strings.edit_label || "Bearbeiten",
                onClick: () => {
                  setText(item.description || "");
                  setPanel("edit");
                },
              },
              { key: "assign", icon: "person_add", label: strings.assign_users || "Benutzer zuweisen", onClick: () => setPanel("assign") },
              // Same glyph the sidebar uses for "Alle Räume".
              onSetRoom
                ? { key: "room", icon: "folder_open", label: strings.cockpit_todo_change_project || "Projekt ändern", onClick: () => setPanel("room") }
                : null,
              // A bookmark has no day yet; a task from the report already has one.
              !scheduled && onSchedule
                ? { key: "schedule", icon: "event", label: strings.cockpit_todo_set_date || "Datum festlegen", onClick: () => setPanel("schedule") }
                : null,
              { key: "delete", icon: "delete", label: strings.delete_label || "Löschen", danger: true, onClick: () => onDelete(item) },
            ]}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Scheduling, renaming, assigning and deleting for a list of bookmarked
 * to-dos. Rows edited here are patched locally so the list does not jump
 * while the surrounding data reloads.
 */
export function useBookmarkActions({ strings, onChanged }) {
  const [busyId, setBusyId] = useState("");
  const [gone, setGone] = useState({});
  const [patches, setPatches] = useState({});
  // In the calendars or not, as the calendar button / a calendar-side delete left it: shown until
  // the server's rows say the same, then the server's value counts again (a later delete in
  // Google / Outlook must turn the button off).
  const calendarRef = useRef({});
  const [, setCalendarTick] = useState(0);

  const patch = (item, fields) =>
    setPatches((current) => ({ ...current, [item.todoId]: { ...(current[item.todoId] || {}), ...fields } }));

  const mutate = async (item, run) => {
    if (!item?.todoId || busyId) return false;
    setBusyId(item.todoId);
    try {
      return await run();
    } catch (_) {
      // Keep the row untouched so the action can be retried.
      return false;
    } finally {
      setBusyId("");
    }
  };

  // Giving a bookmarked priority a day and an hour is what turns it into a task.
  const schedule = (item, date, time) =>
    mutate(item, async () => {
      const { data } = await callAPI(`/todo/${item.todoId}`, {}, "PUT", {
        date: new Date(`${date}T${time || BOOKMARK_DEFAULT_TIME}:00`).toISOString(),
      });
      if (data?.status === false) return false;
      setGone((current) => ({ ...current, [item.todoId]: true }));
      if (onChanged) onChanged(item);
      return true;
    });

  // The tick on a row: my part is done, as on the Aufgaben list (which asks first).
  const complete = async (item) => {
    try {
      await $crud.confirm({
        title: strings.cockpit_todo_mark_done || "Als erledigt markieren",
        textContent: strings.pro_todo_done_confirm || "Möchtest du diese Aufgabe wirklich als erledigt markieren?",
        options: { ok: strings.confirm || "Bestätigen", cancel: strings.cancel || "Abbrechen" },
      });
    } catch (_) {
      return false;
    }
    return mutate(item, async () => {
      const { data } = await callAPI(`/todo/${item.todoId}/user/updateStatus`, {}, "PUT", { status: "completed" });
      if (data?.status === false) return false;
      setGone((current) => ({ ...current, [item.todoId]: true }));
      if (onChanged) onChanged(item);
      return true;
    });
  };

  const rename = (item, description) =>
    mutate(item, async () => {
      const { data } = await callAPI(`/todo/${item.todoId}`, {}, "PUT", { description });
      if (data?.status === false) return false;
      patch(item, { description });
      return true;
    });

  // Moving a task to another project. It is never detached: every task belongs to one.
  const setRoom = (item, room) => {
    if (!room || room.id == null || room.id === "") return Promise.resolve(false);
    return mutate(item, async () => {
      const { data } = await callAPI(`/todo/${item.todoId}`, {}, "PUT", { roomId: room.id });
      if (data?.status === false) return false;
      patch(item, { roomId: room.id, roomName: room.name || "" });
      return true;
    });
  };

  const assign = (item, users) =>
    mutate(item, async () => {
      const { data } = await callAPI(`/todo/${item.todoId}`, {}, "PUT", {
        assignTo: users.map((user) => user.id),
      });
      if (data?.status === false) return false;
      patch(item, { assignedTo: users });
      return true;
    });

  // A report task that names another member is never assigned on its own:
  // the user confirms the proposed person (added to the assignees) or
  // dismisses the proposal.
  const answerSuggestion = (item, accept) =>
    mutate(item, async () => {
      const proposal = item.suggestedAssignee;
      if (!proposal || !proposal.userId) return false;
      const assigned = item.assignedTo || [];
      const [firstName, ...rest] = String(proposal.name || "").split(" ");
      const users = accept
        ? assigned
          .filter((user) => String(user.id) !== String(proposal.userId))
          .concat({ id: proposal.userId, firstName, lastName: rest.join(" ") })
        : assigned;
      const body = { assigneeSuggestion: accept ? "confirmed" : "dismissed" };
      if (accept) body.assignTo = users.map((user) => user.id);
      const { data } = await callAPI(`/todo/${item.todoId}`, {}, "PUT", body);
      if (data?.status === false) return false;
      patch(item, { assignedTo: users, suggestedAssignee: { ...proposal, status: body.assigneeSuggestion } });
      return true;
    });

  const markGone = (items, on) =>
    setGone((current) => {
      const next = { ...current };
      items.forEach((item) => {
        if (on) next[item.todoId] = true;
        else delete next[item.todoId];
      });
      return next;
    });

  const deleteFailed = () =>
    $crud.notify({
      type: "error",
      message: strings.cockpit_task_delete_failed || "Die Aufgabe konnte nicht gelöscht werden.",
    });

  // The row leaves as soon as the user confirms; it only comes back when the
  // server refuses. `onStart` runs at that moment too (the report row hides).
  const remove = async (item, { onStart } = {}) => {
    try {
      await $crud.confirm({
        title: "",
        textContent:
          strings.delete_bookmarked_priority_confirm
          || "Möchtest du diese vorgemerkte Aufgabe wirklich löschen?",
        options: { ok: strings.delete_label || "Löschen", cancel: strings.cancel || "Abbrechen" },
      });
    } catch (_) {
      return false;
    }
    if (!item?.todoId || busyId) return false;
    if (onStart) onStart();
    markGone([item], true);
    const removed = await mutate(item, async () => {
      const { data } = await callAPI(`/todo/${item.todoId}`, {}, "DELETE");
      if (data?.status === false) return false;
      if (onChanged) onChanged(item);
      return true;
    });
    if (!removed) {
      markGone([item], false);
      deleteFailed();
    }
    return removed;
  };

  /**
   * Deletes a whole selection. The caller has already asked once, for the
   * selection as a whole, so this skips the per-row confirmation; it returns
   * the items that really went, because a row whose delete failed has to stay
   * on the list.
   */
  const removeMany = async (items) => {
    const queue = (items || []).filter((item) => item && item.todoId);
    if (!queue.length) return [];
    // Gone at once; the ones the server kept come back below.
    const all = queue.slice();
    markGone(all, true);
    const done = [];
    // One at a time a long selection takes a visible minute. A few in parallel
    // is enough to feel immediate without flooding the API.
    const worker = async () => {
      while (queue.length) {
        const item = queue.shift();
        try {
          const { data } = await callAPI(`/todo/${item.todoId}`, {}, "DELETE");
          if (data?.status !== false) done.push(item);
        } catch (_) {
          /* one failure must not strand the rest of the selection */
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(6, queue.length) }, worker));
    const kept = all.filter((item) => !done.includes(item));
    if (kept.length) markGone(kept, false);
    if (done.length && onChanged) onChanged(done[done.length - 1]);
    return done;
  };

  // The calendar button changed the row (calendarSynced) until the server agrees.
  const patchItem = (item, fields) => {
    if (!item || !item.todoId || !fields || typeof fields.calendarSynced !== "boolean") return;
    calendarRef.current = { ...calendarRef.current, [item.todoId]: fields.calendarSynced };
    setCalendarTick((tick) => tick + 1);
  };

  // A bookmark that got its day in the calendar's date modal leaves the list.
  const scheduled = (item) => {
    setGone((current) => ({ ...current, [item.todoId]: true }));
    if (onChanged) onChanged(item);
  };

  /** Applies the local edits and drops rows that left the bookmark list. */
  const resolve = (items) =>
    (items || [])
      .filter((item) => item && !gone[item.todoId])
      .map((item) => {
        const next = patches[item.todoId] ? { ...item, ...patches[item.todoId] } : item;
        const synced = calendarRef.current[item.todoId];
        if (synced === undefined) return next;
        if (item.calendarSynced === synced) {
          // The server caught up: its later changes show again.
          delete calendarRef.current[item.todoId];
          return next;
        }
        return { ...next, calendarSynced: synced };
      });

  return { busyId, resolve, schedule, complete, rename, assign, answerSuggestion, setRoom, remove, removeMany, patchItem, scheduled };
}

/**
 * The bookmarked priorities on their own. The report card normally folds them
 * in under its own priorities; this covers the cockpit before a first report.
 */
export function CockpitBookmarkedPriorities({ strings, rooms, items = [], onChanged }) {
  const tasks = useBookmarkActions({ strings, onChanged });
  const calendar = useTodoCalendar({ onChange: tasks.patchItem, onScheduled: tasks.scheduled });
  const open = tasks.resolve(items);
  if (!open.length) return null;
  return (
    <div className="cockpit-briefing-priorities">
      {calendar.modal}
      {open.map((item) => (
        <BookmarkRow
          key={item.todoId}
          item={item}
          strings={strings}
          rooms={rooms}
          busy={tasks.busyId === item.todoId}
          onComplete={tasks.complete}
          onSchedule={tasks.schedule}
          onRename={tasks.rename}
          onAssign={tasks.assign}
          onAnswerSuggestion={tasks.answerSuggestion}
          onSetRoom={tasks.setRoom}
          onDelete={tasks.remove}
          calendar={calendar.optionFor(item)}
          onCalendar={calendar.toggle}
        />
      ))}
    </div>
  );
}
